import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { ConceptType, PersonRole } from "@ha/data/basic";
import { HaModalService } from "@ha/ui/common";
import { Select, Store } from "@ngxs/store";
import { DeviceDetectorService } from "ngx-device-detector";
import { Observable, Subject } from "rxjs";

import { CheckboxComponent, SearchBoxComponent } from "@ha/ui/forms";
import { validatePnr } from "@ha/util/common";
import { PermitCreateSelectors } from "../../../permit-create/states/permit-create.selectors";
import { PermitPersonWithStatus } from "../../../shared/models/permit-person.model";
import { Concept1InputData, Concept2ByCertificateIdData, Concept2ByPersonNumberData, OtherCountryByCardNumberOrEmailData, SearchPersonForPermitCommand } from "../../models/permit-team-commands.model";
import { PersonSearchState } from "../../models/person-search-state.model";
import { PermitTeamActions } from "../../states/permit-team.action";
import { PermitTeamSelectors } from "../../states/permit-team.selectors";
import { PermitTeamSearch } from "../../states/permit-team.state";
import { CertificateIdentifierHintModalComponent } from "../certificate-identifier-hint/certificate-identifier-hint.component";
import { ScanCardModalComponent, ScanCardModalComponentData } from "../scan-card-modal/scan-card-modal.component";

export enum FindPersonView {
    Start = 0,
    CreateCertificate = 1,
    Concept1 = 2,
    Concept2 = 3,
    Concept2ByPersonNumber = 4,
    Concept3 = 5,
    Concept4 = 6,
    Concept5 = 7,
    Other = 8,
}

@Component({
    selector: "pmt-find-person",
    templateUrl: "find-person.component.html",
    styleUrls: ["find-person.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FindPersonComponent implements OnInit {
    @Input()
    public allowNewPersons: boolean;

    @Input()
    public permitId: number;

    @Input()
    public role: PersonRole;

    @Output()
    createCertificateEvent1 = new EventEmitter<PermitPersonWithStatus>();

    @ViewChild("query")
    private queryInput: SearchBoxComponent;

    @ViewChild("checkbox")
    private checkbox: CheckboxComponent;

    @Select(PermitTeamSelectors.searchResult)
    public searchResult$: Observable<PermitTeamSearch>;

    public get isDesktop(): boolean {
        return this.deviceService.isDesktop();
    }

    public currentConcept: ConceptType;
    public ConceptType = ConceptType;
    public query$: Subject<string> = new Subject<string>();

    public brandfarligaArbeten = ConceptType.BrandfarligaArbeten;

    public PersonRole = PersonRole;

    public isPremium: boolean;
    public hasForeignPersonalNumber: boolean = false;
    public allowExternalCertificates: boolean;
    public isQueryValid: boolean = true;
    public startCreate: boolean = false;

    public View = FindPersonView;
    public currentView: FindPersonView = FindPersonView.Start;
    public previousView: FindPersonView = FindPersonView.Start;
    public lastQuery: string = "";

    constructor(
        private store: Store,
        private modalService: HaModalService,
        private deviceService: DeviceDetectorService,
        private cdr: ChangeDetectorRef,
    ) {
    }

    public ngOnInit() {
        this.store.dispatch(new PermitTeamActions.ResetFind());
        this.store.dispatch(new PermitTeamActions.ResetNewPersonForm());

        this.isPremium = this.store.selectSnapshot(PermitCreateSelectors.isPremium);
        this.allowExternalCertificates = this.store.selectSnapshot(PermitCreateSelectors.allowExternalCertificates);

        if (!this.allowExternalCertificates) {
            this.currentView = FindPersonView.Concept1;
        }

        this.searchResult$.subscribe((result) => {
            if (result.errorMessage && this.currentView === FindPersonView.Concept2) {
                this.changeView(FindPersonView.Concept2ByPersonNumber);
                this.store.dispatch(new PermitTeamActions.ResetFind());
            }

            if (this.currentView === FindPersonView.Concept2ByPersonNumber) {
                const shouldDisableControls = !!result.errorMessage || !!result.person;
                this.queryInput.setDisabledState(shouldDisableControls);
                this.checkbox.setDisabledState(shouldDisableControls);
            }
        });
    }

    public openCertificateIdentifierHintModal(event: MouseEvent): void {
        const element = event.target as HTMLElement;
        if (element.tagName.toLowerCase() === "a") {
            this.modalService.open(CertificateIdentifierHintModalComponent, true);
        }
    }

    public personStaged(certificateId: number): boolean {
        const stagedPersons = this.store.selectSnapshot(PermitTeamSelectors.stagedPersons);
        if (stagedPersons) {
            return stagedPersons.find((_) => _.certificateId == certificateId) != undefined;
        }

        return false;
    }

    private toPersonSearchState(currentView: FindPersonView, concept: ConceptType): PersonSearchState {
        if (currentView == FindPersonView.Concept1) {
            return PersonSearchState.Concept1;
        }
        if (currentView == FindPersonView.Concept2) {
            return PersonSearchState.Concept2ByCertificateId;
        }
        if (currentView == FindPersonView.Concept2ByPersonNumber) {
            return PersonSearchState.Concept2ByPersonNumber;
        }
        if (currentView == FindPersonView.Concept3) {
            return PersonSearchState.DenmarkByEmail;
        }
        if (currentView == FindPersonView.Concept4) {
            return PersonSearchState.FinlandByShortNumber;
        }
        if (currentView == FindPersonView.Concept5) {
            return PersonSearchState.NorwayByShortNumber;
        }
        if (currentView == FindPersonView.CreateCertificate && concept == ConceptType.Finland) {
            return PersonSearchState.FinlandByShortNumberNotFound;
        }
        if (currentView == FindPersonView.CreateCertificate && concept == ConceptType.Norway) {
            return PersonSearchState.NorwayByShortNumberNotFound;
        }
        if (currentView == FindPersonView.CreateCertificate && concept == ConceptType.Denmark) {
            return PersonSearchState.DenmarkByEmailNotFound;
        }
        return PersonSearchState.Concept1;
    }

    private validateQuery(query: string, currentView: FindPersonView): boolean {
        if (currentView == FindPersonView.Concept1) {
            const regex = /^\d{6,7}$/g;
            return validatePnr(query) || regex.test(query);
        }
        if (currentView == FindPersonView.Concept2) {
            const regex =
                /^(?:(?:\d{5,15}|\d{4,7}-\d{4,7}|\d{8}-\d{8}-\d{4}-[A-Za-z]{1}-\d{3})|[A-Za-z]{2,3}(?:-\d{7,12}|\d{4}-\d{4,10})|[A-Za-z]{3}(?:-\d{2}\/\d{3}|-[A-Za-z]{3}\d{6}|-\d{6}-\d{3}|\d{6,7})|[A-Za-z]{2}-(?:[A-Za-z]{2}-[A-Za-z]{1}-\d{2}\/\d{2}|\d{4}-\d{6}))$/g;
            return regex.test(query);
        }
        if (currentView == FindPersonView.Concept2ByPersonNumber) {
            return validatePnr(query);
        }
        if (currentView == FindPersonView.Concept3) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/g;
            return emailRegex.test(query);
        }
        if (currentView == FindPersonView.Concept4) {
            const regex = /^\d{7} ?\d{7}$/g;
            return regex.test(query);
        }
        if (currentView == FindPersonView.Concept5) {
            const regex = /^\d{5,7}$/g;
            return regex.test(query);
        }
        return true;
    }

    public search(query: string) {
        this.store.dispatch(new PermitTeamActions.ResetFind());
        this.isQueryValid = this.validateQuery(query, this.currentView);
        if (!this.isQueryValid) {
            return;
        }

        var command: SearchPersonForPermitCommand = {} as SearchPersonForPermitCommand;
        command.currentState = this.toPersonSearchState(
            this.currentView,
            this.store.selectSnapshot(PermitTeamSelectors.currentConcept),
        ).toString();
        command.permitId = this.permitId;

        if (this.currentView == FindPersonView.Concept1) {
            command.concept1InputData = {} as Concept1InputData;
            command.concept1InputData.concept = ConceptType.HetaArbeten;
            command.concept1InputData.query = query;
        }
        if (this.currentView == FindPersonView.Concept2) {
            command.concept2ByCertificateIdData = {} as Concept2ByCertificateIdData;
            command.concept2ByCertificateIdData.concept = ConceptType.BrandfarligaArbeten;
            command.concept2ByCertificateIdData.certificateIdentifier = query;
            this.store.dispatch(new PermitTeamActions.SetCertificateIdentifier(query));
        }
        if (this.currentView == FindPersonView.Concept2ByPersonNumber) {
            command.concept2ByPersonNumberData = {} as Concept2ByPersonNumberData;
            command.concept2ByPersonNumberData.concept = ConceptType.BrandfarligaArbeten;
            command.concept2ByPersonNumberData.personNumber = query;
            this.store.dispatch(new PermitTeamActions.SetPersonalNumber(query));
        }
        if (this.currentView == FindPersonView.Concept3) {
            command.otherCountryByCardNumberOrEmailData = {} as OtherCountryByCardNumberOrEmailData;
            command.otherCountryByCardNumberOrEmailData.concept = ConceptType.Denmark;
            command.otherCountryByCardNumberOrEmailData.email = query;
            this.store.dispatch(new PermitTeamActions.SetEmail(query));
        }
        if (this.currentView == FindPersonView.Concept4) {
            command.otherCountryByCardNumberOrEmailData = {} as OtherCountryByCardNumberOrEmailData;
            command.otherCountryByCardNumberOrEmailData.concept = ConceptType.Finland;
            command.otherCountryByCardNumberOrEmailData.certificateIdentifier = query;
            this.store.dispatch(new PermitTeamActions.SetCertificateIdentifier(query));
        }
        if (this.currentView == FindPersonView.Concept5) {
            command.otherCountryByCardNumberOrEmailData = {} as OtherCountryByCardNumberOrEmailData;
            command.otherCountryByCardNumberOrEmailData.concept = ConceptType.Norway;
            command.otherCountryByCardNumberOrEmailData.certificateIdentifier = query;
            this.store.dispatch(new PermitTeamActions.SetCertificateIdentifier(query));
        }
        this.lastQuery = query;
        this.store.dispatch(new PermitTeamActions.SearchPersonForPermit(command));
    }

    public openScanCardModal() {
        this.modalService.open(
            ScanCardModalComponent,
            true,
            <ScanCardModalComponentData> { permitId: this.permitId, role: this.role, isPremium: this.isPremium },
        );
    }

    public goBack() {
        this.startCreate = false;
        let previousView = this.getPreviousView(this.currentView);

        this.store.dispatch(new PermitTeamActions.ResetFind());
        this.store.dispatch(new PermitTeamActions.ResetNewPersonResponse());

        this.changeView(previousView);

        if (previousView == FindPersonView.Concept2ByPersonNumber) {
            this.store.dispatch(new PermitTeamActions.ResetNewPersonFormKeepCertId());
        } else {
            this.store.dispatch(new PermitTeamActions.ResetNewPersonForm());
        }
        this.hasForeignPersonalNumber = false;
        this.queryInput.reset();
        this.query$.next("");
        this.queryInput.setDisabledState(false);
        this.checkbox.setDisabledState(false);
    }

    public changeView(view: FindPersonView) {
        this.isQueryValid = true;
        if (view == FindPersonView.Concept2 || view == FindPersonView.Concept2ByPersonNumber) {
            this.currentConcept = ConceptType.BrandfarligaArbeten;
        }
        if (view == FindPersonView.Concept1) {
            this.currentConcept = ConceptType.HetaArbeten;
        }
        if (view == FindPersonView.Concept3) {
            this.currentConcept = ConceptType.Denmark;
        }
        if (view == FindPersonView.Concept4) {
            this.currentConcept = ConceptType.Finland;
        }
        if (view == FindPersonView.Concept5) {
            this.currentConcept = ConceptType.Norway;
        }

        this.previousView = this.currentView;
        this.currentView = view;

        this.store.dispatch(new PermitTeamActions.SetConcept(this.currentConcept));
    }

    public changeViewWithFoundPerson(view: FindPersonView) {
        let person = this.store.selectSnapshot(PermitTeamSelectors.searchResult).person;
        this.store.dispatch(new PermitTeamActions.SetNewPersonForm(person));
        this.changeView(view);
    }

    public createCertificate(person: PermitPersonWithStatus) {
        this.createCertificateEvent1.emit(person);
    }

    private getPreviousView(view: FindPersonView): FindPersonView {
        let concept = this.store.selectSnapshot(PermitTeamSelectors.currentConcept);

        if (view == FindPersonView.Concept3 || view == FindPersonView.Concept4 || view == FindPersonView.Concept5) {
            return FindPersonView.Other;
        }

        if (view == FindPersonView.Concept2ByPersonNumber) {
            return FindPersonView.Concept2;
        }

        if (view == FindPersonView.CreateCertificate) {
            if (concept == ConceptType.BrandfarligaArbeten) {
                return FindPersonView.Concept2ByPersonNumber;
            }
            if (concept == ConceptType.Denmark) {
                return FindPersonView.Concept3;
            }

            if (concept == ConceptType.Finland) {
                return FindPersonView.Concept4;
            }

            if (concept == ConceptType.Norway) {
                return FindPersonView.Concept5;
            }
        }

        return FindPersonView.Start;
    }

    public setHasForeignPersonalNumber(): void {
        this.queryInput.setDisabledState(this.hasForeignPersonalNumber);
        if (this.hasForeignPersonalNumber) {
            this.queryInput.reset();
            this.query$.next("");
        }
        this.store.dispatch(new PermitTeamActions.SetHasForeignPersonalNumber(this.hasForeignPersonalNumber));
        this.cdr.detectChanges();
    }
}
